import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { parseGenres } from "@/data/gameGenres";
import { AlertTriangle, Key, Gamepad2, TrendingUp, Clock, Trophy, User, Link2, ShieldCheck } from "lucide-react";
import { addReport, removeReport, hasDeveloperReportedCreator, getCreatorReportDetails, formatReportDate, getCreatorTrustScore, getTrustScoreEmoji, getTrustScoreColor, getTrustScoreLabel } from "@/lib/reportUtils";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { SteamProfileBlock } from "@/components/SteamProfileBlock";
import { useEffect, useState } from "react";
import { getCreatorStats, CreatorStats as CreatorStatsType } from "@/services/pointsService";
import { formatPlaytime } from "@/services/steamPlaytimeService";

interface Creator {
  id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  channel_url: string | null;
  favorite_genres: string | null;
  twitch_url: string | null;
  youtube_url: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  twitter_url: string | null;
  bluesky_url: string | null;
  steam_url: string | null;
  steam_id: string | null;
  is_verified?: boolean | null;
  // Twitch stats
  twitch_followers_count?: number;
  twitch_subscribers_count?: number;
  twitch_views_count?: number;
  // YouTube stats
  youtube_subscribers_count?: number;
  youtube_views_count?: number;
  has_reported?: boolean;
  report_details?: {
    total_reports: number;
    latest_report_date: string | null;
  };
  trust_score?: number;
}

interface CreatorProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  creator: Creator | null;
  userType?: string;
  onCreatorUpdate?: () => void;
}

export const CreatorProfileModal = ({ isOpen, onClose, creator, userType, onCreatorUpdate }: CreatorProfileModalProps) => {
  const { user } = useAuth();
  const [creatorStats, setCreatorStats] = useState<CreatorStatsType | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (isOpen && creator?.id) {
      loadCreatorStats();
    } else {
      setCreatorStats(null);
    }
  }, [isOpen, creator?.id]);

  const loadCreatorStats = async () => {
    if (!creator?.id) return;

    setLoadingStats(true);
    try {
      const stats = await getCreatorStats(creator.id);
      if (stats) {
        setCreatorStats(stats);
      }
    } catch (error) {
      console.error('Error loading creator stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  if (!creator) return null;


  const handleReportCreator = async (isReporting: boolean) => {
    if (!user?.id) return;

    try {
      if (isReporting) {
        await addReport(user.id, creator.id);
        toast.success('Creator reported successfully');
      } else {
        await removeReport(user.id, creator.id);
        toast.success('Creator unreported successfully');
      }
      if (onCreatorUpdate) {
        onCreatorUpdate();
      }
    } catch (error) {
      console.error('Error reporting creator:', error);
      toast.error('Failed to report creator');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card max-w-2xl h-[90vh] flex flex-col p-0 sm:max-w-2xl rounded-lg">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex-shrink-0 border-b">
          <DialogTitle className="text-lg sm:text-xl">Creator Profile</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="overview" className="w-full">
            {/* Header Section - Avatar and Name with Trust Score */}
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b bg-muted/20">
              <div className="flex items-center gap-3 sm:gap-4">
                {creator.avatar_url ? (
                  <img
                    src={creator.avatar_url}
                    alt={creator.display_name}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-xl sm:text-2xl font-bold">
                      {creator.display_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 mb-1 sm:mb-2 flex-wrap">
                    <h3 className="text-xl sm:text-2xl font-bold truncate">{creator.display_name}</h3>
                    {creator.is_verified && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex-shrink-0">
                              <ShieldCheck className="w-5 h-5 text-blue-500" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Verified User</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {creator.trust_score !== undefined && userType === 'developer' && (
                    <Badge
                      variant="outline"
                      className={`text-xs ${getTrustScoreColor(creator.trust_score)} border-current w-fit`}
                    >
                      {getTrustScoreEmoji(creator.trust_score)} {getTrustScoreLabel(creator.trust_score)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <TabsList className="w-full rounded-none border-b justify-start px-4 sm:px-6 h-auto overflow-x-auto">
              <TabsTrigger value="overview" className="p-1 px-2 text-sm whitespace-nowrap">Overview</TabsTrigger>
              <TabsTrigger value="channels" className="p-1 px-2 text-sm whitespace-nowrap">Channels</TabsTrigger>
              {userType === 'developer' && (
                <TabsTrigger value="management" className="p-1 px-2 text-sm whitespace-nowrap">Management</TabsTrigger>
              )}
            </TabsList>

            <div className="px-4 sm:px-6 py-4 sm:py-6">
              <TabsContent value="overview" className="space-y-4 mt-0">
                {/* Stats Grid - Highlighted */}
                {creatorStats && (
                  <>
                    {loadingStats ? (
                      <Card className="p-4 sm:p-6">
                        <div className="text-center text-muted-foreground text-sm">Loading stats...</div>
                      </Card>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <Card className="p-2 bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-green-500/20 rounded-lg flex-shrink-0">
                              <Key className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 truncate">Keys Received</p>
                              <p className="text-sm sm:text-lg font-bold">{creatorStats.keys_received}</p>
                            </div>
                          </div>
                        </Card>
                        <Card className="p-2 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-purple-500/20 rounded-lg flex-shrink-0">
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 truncate">Received games Playtime</p>
                              <p className="text-sm sm:text-lg font-bold truncate">{formatPlaytime(creatorStats.total_playtime)}</p>
                            </div>
                          </div>
                        </Card>

                        <Card className="p-2 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                              <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 truncate">Games Covered</p>
                              <p className="text-sm sm:text-lg font-bold">{creatorStats.games_covered}</p>
                            </div>
                          </div>
                        </Card>

                        <Card className="p-2 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 truncate">Content</p>
                              <p className="text-sm sm:text-lg font-bold">{creatorStats.content_created}</p>
                            </div>
                          </div>
                        </Card>

                      </div>
                    )}

                    <Separator />
                  </>
                )}

                {/* Bio Section */}
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <h4 className="font-semibold text-base">About</h4>
                  </div>
                  {creator.bio ? (
                    <p className="md:text-sm text-[12px] text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {creator.bio}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No bio available</p>
                  )}
                </Card>

                {/* Favorite Genres */}
                {creator.favorite_genres && (
                  <Card className="p-4">
                    <h4 className="font-semibold mb-3 text-base">Favorite Genres</h4>
                    <div className="flex flex-wrap gap-2">
                      {parseGenres(creator.favorite_genres).map((genre) => (
                        <Badge key={genre} variant="secondary" className="text-xs px-3 py-1">
                          {genre}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="channels" className="space-y-4 mt-0">
                {(creator.twitch_url || creator.youtube_url || creator.tiktok_url || creator.instagram_url || creator.twitter_url || creator.bluesky_url || creator.steam_url || creator.steam_id) ? (
                  <div className="space-y-3">
                    <h4 className="font-semibold mb-3 sm:mb-4 text-base sm:text-lg flex items-center gap-2">
                      <Link2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      Social Channels
                    </h4>

                    <div className="flex flex-col gap-2">
                      {/* Twitch */}
                      {creator.twitch_url && (
                        <Card className="p-2 bg-gradient-to-r from-purple-500/10 to-purple-600/5 border-purple-500/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-purple-500/20 rounded-full flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.twitch_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-purple-600 hover:text-purple-700 hover:underline text-sm sm:text-base block truncate"
                                >
                                  Twitch
                                </a>
                                {((creator.twitch_followers_count != null && creator.twitch_followers_count > 0) ||
                                  (creator.twitch_subscribers_count != null && creator.twitch_subscribers_count > 0) ||
                                  (creator.twitch_views_count != null && creator.twitch_views_count > 0)) && (
                                    <div className="flex flex-wrap gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground mt-1">
                                      {creator.twitch_followers_count != null && creator.twitch_followers_count > 0 && (
                                        <span>{creator.twitch_followers_count.toLocaleString()} followers</span>
                                      )}
                                      {creator.twitch_subscribers_count != null && creator.twitch_subscribers_count > 0 && (
                                        <span>{creator.twitch_subscribers_count.toLocaleString()} subscribers</span>
                                      )}
                                      {creator.twitch_views_count != null && creator.twitch_views_count > 0 && (
                                        <span>{creator.twitch_views_count.toLocaleString()} views</span>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* YouTube */}
                      {creator.youtube_url && (
                        <Card className="p-2 bg-gradient-to-r from-red-500/10 to-red-600/5 border-red-500/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-red-500/20 rounded-full flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.youtube_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-red-600 hover:text-red-700 hover:underline text-sm sm:text-base block truncate"
                                >
                                  YouTube
                                </a>
                                {((creator.youtube_subscribers_count != null && creator.youtube_subscribers_count > 0) ||
                                  (creator.youtube_views_count != null && creator.youtube_views_count > 0)) && (
                                    <div className="flex flex-wrap gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground mt-1">
                                      {creator.youtube_subscribers_count != null && creator.youtube_subscribers_count > 0 && (
                                        <span>{creator.youtube_subscribers_count.toLocaleString()} subscribers</span>
                                      )}
                                      {creator.youtube_views_count != null && creator.youtube_views_count > 0 && (
                                        <span>{creator.youtube_views_count.toLocaleString()} views</span>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* TikTok */}
                      {creator.tiktok_url && (
                        <Card className="p-2 bg-gradient-to-r from-pink-500/10 to-pink-600/5 border-pink-500/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-pink-500/20 rounded-full flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.tiktok_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-pink-600 hover:text-pink-700 hover:underline text-sm sm:text-base block truncate"
                                >
                                  TikTok
                                </a>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* Steam Profile */}
                      {(creator.steam_url || creator.steam_id) && (
                        <Card className="p-2 bg-muted/50 rounded-custom-paddings">
                          <SteamProfileBlock
                            steamId={creator.steam_id}
                            steamUrl={creator.steam_url}
                          />
                        </Card>
                      )}

                      {/* Instagram */}
                      {creator.instagram_url && (
                        <Card className="p-2 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-pink-600/5 border-pink-500/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-pink-500/20 rounded-lg flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.instagram_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-pink-600 hover:text-pink-700 hover:underline text-sm sm:text-base block truncate"
                                >
                                  Instagram
                                </a>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* X (Twitter) */}
                      {creator.twitter_url && creator.twitter_url.trim() !== '' && (
                        <Card className="p-2 bg-gradient-to-r from-background/90 to-[#2a475e]/5 border-slate-700/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-slate-700/20 rounded-full flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.twitter_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-slate-300 hover:text-slate-100 hover:underline text-sm sm:text-base block truncate"
                                >
                                  X
                                </a>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}

                      {/* Bluesky */}
                      {creator.bluesky_url && (
                        <Card className="p-2 bg-gradient-to-r from-sky-500/10 to-sky-600/5 border-sky-500/20 rounded-custom-paddings">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="p-1.5 sm:p-2 bg-sky-500/20 rounded-full flex-shrink-0">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 10.8c-1.087-2.183-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.22-.254.455-.345.705-.18.5-.275 1.03-.275 1.58 0 1.418.488 2.73 1.375 3.78.89 1.05 2.205 1.65 3.625 1.65s2.735-.6 3.625-1.65c.887-1.05 1.375-2.362 1.375-3.78 0-.55-.095-1.08-.275-1.58-.091-.25-.207-.485-.345-.705.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.79.624-6.478 0-.69-.139-2.861-.902-3.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.617 12 10.8z" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <a
                                  href={creator.bluesky_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-semibold text-sky-600 hover:text-sky-700 hover:underline text-sm sm:text-base block truncate"
                                >
                                  Bluesky
                                </a>
                              </div>
                            </div>
                          </div>
                        </Card>
                      )}
                    </div>
                  </div>
                ) : (
                  <Card className="p-8 text-center">
                    <p className="text-muted-foreground">No social media channels available</p>
                  </Card>
                )}
              </TabsContent>

              {userType === 'developer' && (
                <TabsContent value="management" className="space-y-4 mt-0">
                  <Card className="p-4 border-destructive/20">
                    <h4 className="font-semibold mb-3 text-base">Creator Management</h4>
                    <div className="flex justify-center">
                      {creator.has_reported ? (
                        <div className="text-sm text-muted-foreground">
                          ⚠️ Reported: {creator.report_details?.latest_report_date ?
                            formatReportDate(creator.report_details.latest_report_date) :
                            'Unknown date'
                          }
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReportCreator(true)}
                          className="text-xs"
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Report Creator
                        </Button>
                      )}
                    </div>
                  </Card>
                </TabsContent>
              )}
            </div>
          </Tabs>

        </div>
      </DialogContent>
    </Dialog>
  );
};
